import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../../styles/components/_leads.scss';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { debounce } from 'lodash';
import Tooltip from '../tootip/Tootip';
import { FilterX, Download, MessageCircle, Trash2, CheckCircle } from "lucide-react";
import SiteLeadProvider from '../../infra/provider/siteLeadProvider';
import LeadProvider from '../../infra/provider/leadProvider';
import FinancingSettingsDTO from '../../domain/dto/financingSettingsDTO';
import { SiteLeadData, SiteLeadsResponse } from '../../infra/repository/siteLeadRepository';
import { PDFService } from '../../services/pdfService';
import { Formatter } from '../../utils/formatter';

interface TableData {
    id: string;
    createdAt: string;
    expiryDate: string;
    document: string;
    city: string;
    region: string;
    propertyValue: string;
    familyIncome: string;
    phone: string;
    birthDate: string;
    financingValue: string;
    downPayment: string;
    maxInstallment: string;
}

const LeadsSite = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const [data, setData] = useState<TableData[]>([]);
    const isFetching = useRef(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showOfficializeModal, setShowOfficializeModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [isLoadingConfig, setIsLoadingConfig] = useState(false);

    // Estados para as premissas
    const [financingRate, setFinancingRate] = useState('15');
    const [maxFinancingPercent, setMaxFinancingPercent] = useState('70');
    const [incomeCommitment, setIncomeCommitment] = useState('35');
    const [maxFinancingPeriod, setMaxFinancingPeriod] = useState('420');
    const [maxAge, setMaxAge] = useState('900');

    const siteLeadProvider = SiteLeadProvider.getInstance();
    const leadProvider = LeadProvider.getInstance();

    const getLeads = useCallback(
        async (
            pageNumber = 1,
            searchTerm = ""
        ) => {
            if (isFetching.current) return;
            isFetching.current = true;
            setIsLoading(true);

            try {
                const response: SiteLeadsResponse = await siteLeadProvider.getSiteLeads();

                // Converter dados da API para formato da tabela
                const tableData: TableData[] = response.data.map((lead: SiteLeadData) => ({
                    id: lead.id.toString(),
                    createdAt: new Date(lead.createdAt * 1000).toLocaleDateString('pt-BR'),
                    expiryDate: calculateExpiryDate(lead.createdAt),
                    document: lead.document,
                    city: lead.city,
                    region: `${lead.city} - ${lead.uf || 'N/A'}`, // Concatenação de cidade e UF
                    propertyValue: new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                    }).format(lead.propertyValue / 100),
                    familyIncome: new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                    }).format(lead.familyIncome / 100),
                    phone: lead.phone,
                    birthDate: new Date(lead.birthDate * 1000).toLocaleDateString('pt-BR'),
                    financingValue: new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                    }).format(lead.financingValue / 100),
                    downPayment: new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                    }).format(lead.downPayment / 100),
                    maxInstallment: new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                    }).format(lead.maxInstallment / 100)
                }));

                // Aplicar filtros
                let filteredData = tableData;

                if (searchTerm) {
                    filteredData = filteredData.filter(item =>
                        item.document.includes(searchTerm) ||
                        item.phone.includes(searchTerm) ||
                        item.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        item.region.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                }

                // Aplicar paginação
                const startIndex = (pageNumber - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedData = filteredData.slice(startIndex, endIndex);

                setData(paginatedData);
                setTotalItems(response.totalItems);
            } catch (error) {
                toast.error("Erro ao carregar leads do site");
                console.error(error);
            } finally {
                isFetching.current = false;
                setIsLoading(false);
            }
        },
        [itemsPerPage, siteLeadProvider]
    );

    useEffect(() => {
        getLeads(currentPage);
    }, [itemsPerPage, currentPage, getLeads]);

    const loadFinancingSettings = useCallback(async () => {
        setIsLoadingConfig(true);
        try {
            const settings = await leadProvider.getFinancingSettings();

            // Converter os valores para o formato dos inputs (remover decimais e converter para string)
            setFinancingRate((settings.annualFinancingRate * 100).toString());
            setMaxFinancingPercent((settings.maxFinancingPercentage * 100).toString());
            setIncomeCommitment((settings.incomeCommitmentPercentage * 100).toString());
            setMaxFinancingPeriod(settings.maxFinancingTermMonths.toString());
            setMaxAge(settings.maxFinancingAgeMonths.toString());
        } catch (error) {
            toast.error('Erro ao carregar configurações');
            console.error(error);
        } finally {
            setIsLoadingConfig(false);
        }
    }, [leadProvider]);

    useEffect(() => {
        const handleConfigureEvent = () => {
            setShowConfigModal(true);
        };

        window.addEventListener('openConfigModal', handleConfigureEvent);

        return () => {
            window.removeEventListener('openConfigModal', handleConfigureEvent);
        };
    }, []);

    useEffect(() => {
        if (showConfigModal) {
            loadFinancingSettings();
        }
    }, [showConfigModal, loadFinancingSettings]);

    const clearFilters = () => {
        if (searchTerm === "") return;
        setSearchTerm('');
        setCurrentPage(1);
        getLeads(1, "");
    };

    const formatPhone = (phone: string): string => {
        return Formatter.phoneToBR(phone);
    };

    const formatCPF = (cpf: string): string => {
        // Remove tudo que não é dígito
        const cleaned = cpf.replace(/\D/g, '');
        // Aplica a máscara XXX.XXX.XXX-XX
        if (cleaned.length === 11) {
            return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
        }
        return cpf;
    };


    const debouncedSearchTerm = useRef(
        debounce((term) => {
            getLeads(1, term);
        }, 600)
    ).current;

    const handleSearchTermChange = (searchTerm: string) => {
        setSearchTerm(searchTerm);
        debouncedSearchTerm(searchTerm);
    };

    const handleDownloadPDF = async (leadId: string) => {
        try {
            // Buscar dados completos do lead
            const leadData = await siteLeadProvider.getSiteLeadById(leadId);

            // Gerar e baixar PDF
            PDFService.generateLeadPDF(leadData);

            toast.success('PDF baixado com sucesso!');
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            toast.error('Erro ao gerar PDF. Tente novamente.');
        }
    };

    const handleDeleteLead = (leadId: string) => {
        setSelectedLeadId(leadId);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedLeadId) {
            try {
                await siteLeadProvider.deleteSiteLead(selectedLeadId);

                // Remover o lead da tabela
                setData(prevData => prevData.filter(item => item.id !== selectedLeadId));
                setTotalItems(prev => prev - 1);

                toast.success('Lead excluído com sucesso!');
                setShowDeleteModal(false);
                setSelectedLeadId(null);
            } catch (error) {
                toast.error('Erro ao excluir lead');
                console.error(error);
            }
        }
    };

    const handleOfficializeLead = (leadId: string) => {
        setSelectedLeadId(leadId);
        setShowOfficializeModal(true);
    };

    const handleConfirmOfficialize = async () => {
        if (selectedLeadId) {
            try {
                // Oficializar o lead usando o novo método
                await leadProvider.createLeadFromSiteLead(selectedLeadId);

                // Remover o lead da tabela de leads do site
                setData(prevData => prevData.filter(item => item.id !== selectedLeadId));
                setTotalItems(prev => prev - 1);

                toast.success('Lead oficializado com sucesso!');
                setShowOfficializeModal(false);
                setSelectedLeadId(null);
            } catch (error) {
                toast.error('Erro ao oficializar lead');
                console.error(error);
            }
        }
    };

    const handleWhatsAppContact = (phone: string, leadId: string) => {
        // Remove formatação do telefone para WhatsApp
        const cleanPhone = phone.replace(/\D/g, '');
        const whatsappUrl = `https://wa.me/55${cleanPhone}`;

        console.log('Abrindo WhatsApp para lead:', leadId, 'telefone:', phone);
        window.open(whatsappUrl, '_blank');
        toast.success('Contatar');
    };

    const handleApplyConfig = async () => {
        setIsLoadingConfig(true);
        try {
            // Converter os valores dos inputs para o formato da API
            const financingSettings = new FinancingSettingsDTO(
                parseFloat(financingRate) / 100, // Converter de porcentagem para decimal
                parseFloat(maxFinancingPercent) / 100, // Converter de porcentagem para decimal
                parseFloat(incomeCommitment) / 100, // Converter de porcentagem para decimal
                parseInt(maxFinancingPeriod), // Manter como inteiro
                parseInt(maxAge) // Manter como inteiro
            );

            await leadProvider.updateFinancingSettings(financingSettings);

            toast.success('Configurações aplicadas com sucesso!');
            setShowConfigModal(false);
        } catch (error) {
            toast.error('Erro ao salvar configurações');
            console.error(error);
        } finally {
            setIsLoadingConfig(false);
        }
    };

    const calculateExpiryDate = (createdAtTimestamp: number): string => {
        // Converter timestamp Unix para Date
        const date = new Date(createdAtTimestamp * 1000);

        // Adicionar 90 dias
        date.setDate(date.getDate() + 90);

        // Formatar para DD/MM/YYYY
        return date.toLocaleDateString('pt-BR');
    };


    return (
        <div className="data-table-container-leads">
            <div className="filters-container-leads">
                <div className="search-container-leads">
                    <input
                        type="text"
                        placeholder="Busque por CPF, celular, cidade ou região..."
                        value={searchTerm}
                        onChange={(e) => handleSearchTermChange(e.target.value)}
                    />
                </div>
                <Tooltip text="Limpar filtros">
                    <button className="clear-filters-button" onClick={clearFilters}>
                        <FilterX size={16} />
                    </button>
                </Tooltip>
            </div>

            <div className="table-wrapper-leads">
                <table className="modern-table">
                    <thead>
                        <tr>
                            <th>Período</th>
                            <th>Contato</th>
                            <th>Região</th>
                            <th>Valor do imóvel</th>
                            <th>Renda familiar</th>
                            <th>Data nascimento</th>
                            <th>Financiamento</th>
                            <th>Entrada</th>
                            <th>Parcela máxima</th>
                            <th>Ações</th>
                        </tr >
                    </thead >
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                    Carregando...
                                </td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                    Nenhum resultado encontrado.
                                </td>
                            </tr>
                        ) : (
                            data.map((item, index) => (
                                <tr key={index} className="table-row">
                                    <td>
                                        <div className="concatenated-cell">
                                            <div>{item.createdAt}</div>
                                            <div className="secondary-info">{item.expiryDate}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="concatenated-cell">
                                            <div>{formatCPF(item.document)}</div>
                                            <div className="secondary-info">{formatPhone(item.phone)}</div>
                                        </div>
                                    </td>
                                    <td>{item.region}</td>
                                    <td>{item.propertyValue}</td>
                                    <td>{item.familyIncome}</td>
                                    <td>{item.birthDate}</td>
                                    <td>{item.financingValue}</td>
                                    <td>{item.downPayment}</td>
                                    <td>{item.maxInstallment}</td>
                                    <td>
                                        <div className="actions-container">
                                            <Tooltip text="Oficializar">
                                                <button
                                                    className="action-button"
                                                    onClick={() => handleOfficializeLead(item.id)}
                                                    title="Oficializar"
                                                >
                                                    <CheckCircle size={18} />
                                                </button>
                                            </Tooltip>

                                            <Tooltip text="Contatar">
                                                <button
                                                    className="action-button"
                                                    onClick={() => handleWhatsAppContact(item.phone, item.id)}
                                                    title="WhatsApp"
                                                >
                                                    <MessageCircle size={18} />
                                                </button>
                                            </Tooltip>

                                            {/* <Tooltip text="Baixar PDF">
                                                <button
                                                    className="action-button"
                                                    onClick={() => handleDownloadPDF(item.id)}
                                                    title="Baixar"
                                                >
                                                    <Download size={18} />
                                                </button>
                                            </Tooltip> */}

                                            <Tooltip text="Excluir">
                                                <button
                                                    className="action-button"
                                                    onClick={() => handleDeleteLead(item.id)}
                                                    title="Excluir"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr >
                            ))
                        )}
                    </tbody >
                </table >
            </div >

            <div className="table-footer-leads">
                <div className="items-per-page-leads">
                    <span>Exibir por</span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                    </select>
                </div>
                <div className="pagination-leads">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                    >
                        Anterior
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={currentPage === page ? 'active' : ''}
                        >
                            {page}
                        </button>
                    ))}
                    <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                    >
                        Próximo
                    </button>
                </div>
            </div>

            {/* Modal para oficializar lead */}
            {
                showOfficializeModal && (
                    <div className="modal-overlay-faq">
                        <div className="modal-faq">
                            <h2 className="data-box-details-h2">
                                Deseja oficializar este lead?
                            </h2>
                            <p>Ao oficializar este lead, ele passará a ser exibido como um lead oficial na tela de leads.</p>
                            <div className="modal-buttons-faq">
                                <button
                                    onClick={() => {
                                        setShowOfficializeModal(false);
                                        setSelectedLeadId(null);
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button onClick={handleConfirmOfficialize}>
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Modal para excluir lead */}
            {
                showDeleteModal && (
                    <div className="modal-overlay-faq">
                        <div className="modal-faq">
                            <h2 className="data-box-details-h2">
                                Deseja excluir este lead?
                            </h2>
                            <p>Ao excluir este lead, ele deixará de ser exibido dentro da plataforma da homecorp.</p>
                            <div className="modal-buttons-faq">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setSelectedLeadId(null);
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button onClick={handleConfirmDelete}>
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Modal para configurar premissas */}
            {
                showConfigModal && (
                    <div className="modal-overlay-faq">
                        <div className="modal-faq modal-config">
                            <h2 className="data-box-details-h2">
                                Configurar premissas
                            </h2>
                            <p>Defina as premissas para cálculo de financiamento dos leads do site.</p>
                            <div className="divisor-enterprise"></div>

                            <div className="config-form">
                                {isLoadingConfig ? (
                                    <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                        Carregando configurações...
                                    </div>
                                ) : (
                                    <>
                                        <div className="form-group-config">
                                            <label>Taxa de financiamento:</label>
                                            <div className="input-with-suffix">
                                                <input
                                                    type="number"
                                                    value={financingRate}
                                                    onChange={(e) => setFinancingRate(e.target.value)}
                                                    placeholder="15"
                                                    disabled={isLoadingConfig}
                                                />
                                                <span className="suffix">% a.a.</span>
                                            </div>
                                        </div>

                                        <div className="form-group-config">
                                            <label>Financiamento máx. do imóvel:</label>
                                            <div className="input-with-suffix">
                                                <input
                                                    type="number"
                                                    value={maxFinancingPercent}
                                                    onChange={(e) => setMaxFinancingPercent(e.target.value)}
                                                    placeholder="70"
                                                    disabled={isLoadingConfig}
                                                />
                                                <span className="suffix">%</span>
                                            </div>
                                        </div>

                                        <div className="form-group-config">
                                            <label>Comprometimento da renda:</label>
                                            <div className="input-with-suffix">
                                                <input
                                                    type="number"
                                                    value={incomeCommitment}
                                                    onChange={(e) => setIncomeCommitment(e.target.value)}
                                                    placeholder="35"
                                                    disabled={isLoadingConfig}
                                                />
                                                <span className="suffix">%</span>
                                            </div>
                                        </div>

                                        <div className="form-group-config">
                                            <label>Prazo máx. de financiamento:</label>
                                            <div className="input-with-suffix">
                                                <input
                                                    type="number"
                                                    value={maxFinancingPeriod}
                                                    onChange={(e) => setMaxFinancingPeriod(e.target.value)}
                                                    placeholder="420"
                                                    disabled={isLoadingConfig}
                                                />
                                                <span className="suffix">meses</span>
                                            </div>
                                        </div>

                                        <div className="form-group-config">
                                            <label>Idade máx. para financiamento:</label>
                                            <div className="input-with-suffix">
                                                <input
                                                    type="number"
                                                    value={maxAge}
                                                    onChange={(e) => setMaxAge(e.target.value)}
                                                    placeholder="900"
                                                    disabled={isLoadingConfig}
                                                />
                                                <span className="suffix">meses</span>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="modal-buttons-faq">
                                <button
                                    onClick={() => setShowConfigModal(false)}
                                    disabled={isLoadingConfig}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleApplyConfig}
                                    disabled={isLoadingConfig}
                                >
                                    {isLoadingConfig ? 'Salvando...' : 'Aplicar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            <ToastContainer />
        </div >

    );
};

export default LeadsSite;

